<template>
  <div>
    <v-list>
      <div v-for="(keys, section) in sections" :key="section">
        <v-divider></v-divider>
        <v-subheader>{{ section }}</v-subheader>
        <v-list-item
          v-for="key in keys"
          :key="key"
          @click="fields[key].immutable ? null : editField(key)"
        >
          <v-list-item-content>
            <v-list-item-title>
              <template v-if="fields[key].type === Date">
                {{ (get(registration, key) || fields[key].placeholder || null) | formatDate }}
              </template>
              <template v-else-if="fields[key].type === Boolean">
                {{ Boolean(get(registration, key, false)) | yesNo }}
              </template>
              <template v-else>
                {{ get(registration, key) || fields[key].placeholder || 'Not configured' }}
              </template>
            </v-list-item-title>
            <v-list-item-subtitle>{{ fields[key].label || key }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </div>
    </v-list>

    <v-dialog
      v-model="editDialog"
      width="500"
    >
      <v-card>
        <v-card-text>
          <v-row class="pt-5">
            <v-col v-if="editedKey">
              <v-text-field
                v-if="fields[editedKey].type === String"
                :name="editedKey"
                :label="fields[editedKey].label || editedKey"
                v-model="editedValue"
              />
              <v-text-field
                v-if="fields[editedKey].type === Number"
                type="number"
                :name="editedKey"
                :label="fields[editedKey].label || editedKey"
                v-model.number="editedValue"
              />
              <v-checkbox
                v-if="fields[editedKey].type === Boolean"
                :name="editedKey"
                :label="fields[editedKey].label || editedKey"
                v-model="editedValue"
              />
              <v-date-picker
                v-if="fields[editedKey].type === Date"
                :name="editedKey"
                :label="fields[editedKey].label || editedKey"
                v-model="editedValue"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            color="primary"
            text
            @click="closeEditDialog"
          >
            Save
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="editDialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { get, clone } from 'lodash'
import { formatISO } from 'date-fns'

export default {
  name: 'RegistrationMembershipDetails',

  props: {
    registrationId: {
      type: String,
      required: true
    }
  },
  data: () => ({
    registration: {},
    placeholder: '-',
    editDialog: false,
    editedKey: null,
    editedValue: null,
    sections: {
      Identifiers: [
        'applicationNumber',
        'memberNumber',
        'memberName',
        'abn',
        'entityName',
        'tradingName',
        'businessName'
      ],
      Admin: [
        'accountManager',
        'customerRating',
        'refusesTraining',
        'endUserAssessmentComplete',
        'producerAssessmentComplete',
        'dateAccredited',
        'stampNumber'
      ],
      Legacy: [
        'legacy.enteredBy',
        'legacy.modifiedBy',
        'legacy.endUserIntegrityRating',
        'legacy.finalPre020GradeDate',
        'legacy.stagingBovineChain',
        'legacy.stagingOvineChain',
        'legacy.licenceNumber'
      ]
    },
    fields: {
      applicationNumber: {
        label: 'Application number',
        type: String,
        immutable: true
      },
      memberNumber: {
        label: 'Member number',
        type: String,
        immutable: true
      },
      memberName: {
        label: 'Member name',
        type: String
      },
      abn: {
        label: 'Australian Business Number',
        type: String
      },
      entityName: {
        label: 'Entity name',
        type: String
      },
      tradingName: {
        label: 'Trading name',
        type: String
      },
      businessName: {
        label: 'Business name',
        type: String
      },
      'legacy.enteredBy': {
        label: 'Entered by',
        type: String
      },
      'legacy.modifiedBy': {
        label: 'Modified by',
        type: String
      },
      'legacy.endUserIntegrityRating': {
        label: 'End user integrity rating',
        type: Number
      },
      'legacy.finalPre020GradeDate': {
        label: 'Final pre-020 grade date',
        type: Date
      },
      'legacy.stagingBovineChain': {
        label: 'Staging bovine chain number',
        type: Number
      },
      'legacy.stagingOvineChain': {
        label: 'Staging ovine chain number',
        type: Number
      },
      'legacy.licenceNumber': {
        label: 'End-User Licence Number',
        type: String
      },
      accountManager: {
        label: 'Account manager (TDO)',
        type: String
      },
      customerRating: {
        label: 'Customer rating',
        type: Number
      },
      refusesTraining: {
        label: 'Refuses training',
        type: Boolean
      },
      endUserAssessmentComplete: {
        label: 'End-user assessment complete',
        type: Boolean
      },
      producerAssessmentComplete: {
        label: 'Producer assessment complete',
        type: Boolean
      },
      dateAccredited: {
        label: 'Date accredited',
        type: Date
      },
      stampNumber: {
        label: 'Stamp number',
        type: String
      }
    }
  }),

  apollo: {
    registration: {
      query: gql`query GetRegistration($id: MongoID!) {
        registration: registrationById(_id: $id) {
          _id
          applicationNumber
          memberNumber
          memberName
          abn
          entityName
          tradingName
          businessName
          legacy {
            enteredBy
            modifiedBy
            endUserIntegrityRating
            finalPre020GradeDate
            stagingBovineChain
            stagingOvineChain
            licenceNumber
          }
          accountManager
          customerRating
          refusesTraining
          dateAccredited
          stampNumber
          endUserAssessmentComplete
          producerAssessmentComplete
        }
      }`,
      variables () {
        return {
          id: this.registrationId
        }
      }
    }
  },

  methods: {
    get,

    editField (key) {
      const field = this.fields[key]
      this.editedValue = clone(get(this.registration, key, null))
      if (this.editedValue && field.type === Date) { // vuetify date-picker accepts ISO-8601 **date** strings
        this.editedValue = formatISO(new Date(this.editedValue), { representation: 'date' })
      }
      this.editedKey = key
      this.editDialog = true
    },

    async closeEditDialog () {
      try {
        await this.saveEditedValue()
        this.$emit('change')
      } catch (e) {
        this.$emit('error', e)
        return
      }
      const label = this.fields[this.editedKey].label || this.editedKey
      this.$emit('info', `${label} saved.`)
      this.editDialog = false
      this.editedKey = null
      this.editedValue = null
    },

    async saveEditedValue () {
      await this.$apollo.mutate({
        mutation: gql`mutation ($id: MongoID!, $record: UpdateByIdRegistrationInput!) {
          registrationUpdateById(_id: $id, record: $record){
            record {
              _id
            }
          }
        }`,
        variables: {
          id: this.registrationId,
          record: {
            [this.editedKey]: this.editedValue
          }
        }
      })
      this.$apollo.queries.registration.refresh()
    }
  }
}
</script>
